import { useCallback } from 'react';
import { BsCheckLg } from 'react-icons/bs';
import classNames from 'classnames';
import { Button, Credit, Flex, ReservePriceIcon } from '../../common';
import { AiOutlineQrcode } from 'react-icons/ai';
import timeZeroFix from '@/utils/timeZeroFix';
import { useCurrentReservesContext } from '@/context';
import { useTranslation } from 'react-i18next';

export interface DailySellListItemProps {
  item: DailySaleProgramEntity;
  onIncrement: (item: DailySaleProgramEntity, count: number) => void;
  onRequestForget: (item: DailySaleProgramEntity) => void;
}

const DailySellListItem = ({ onRequestForget, ...props }: DailySellListItemProps) => {

  const count = props.item.selectedCount || 0;
  const { items } = useCurrentReservesContext();
  const isActive = props.item.selectedCount && props.item.selectedCount > 0;

  const isIncDisabled = () => count >= props.item.maxValue;
  const [t] = useTranslation(['common', 'reserve']);

  const itemContainerClass = classNames('dailysale-item', {
    'dailysale-item--enabled':
      props.item.selectedCount && props.item.selectedCount > 0,
  });

  const onForgetButtonClick = useCallback(() => {
    const reserveItem = items.find(item => {
      const reserve = item?.reserve;
      return !!reserve?.reserveDailySaleId && reserve?.dailySellProgramId === props.item.id;
    });
    if (!reserveItem) return;
    onRequestForget({
      ...reserveItem.reserve,
      id: reserveItem?.reserve?.reserveDailySaleId,
      count: reserveItem.reserve.selectedCount
    });
  }, [items, onRequestForget, props?.item?.id]);

  return (
    <div className={itemContainerClass}>
      <div className="dailysale-item-self">{props.item.selfName}</div>
      <div className="dailysale-item-mealType">
        <div>{props.item.mealTypeName}</div>
        <div className="px-3">{props.item.foodTypeTitle}</div>
      </div>
      <div className="mt-2">
        <div className="dailysale-item-food">
          {props.item.foodNames.split('|')[0]}
        </div>
        <div className="dailysale-item-besideFood">
          {props.item.foodNames.split('|')[1]}
        </div>
      </div>
      <Flex direction="column" gap={0} className="mt-auto">
        <div className="dailysale-item-divider" />
        <div className="flex items-center justify-between">
          <div className="flex items-center justify-center">
            <Button
              className={`btn dailysale-button ${isActive && 'is-active'}`}
              disabled={isIncDisabled()}
              onClick={() => props.onIncrement(props?.item, count + 1)}>
              <span className="flaticon-plus" />
            </Button>
            <div className="dailysale-count">
              {t('common:labels.count-of', { value: count })}
            </div>
          </div>
          <Flex gap="0.25rem" className="flex items-center justify-between">
            <ReservePriceIcon className="text-app-orange" />
            <Credit
              inline
              numColor="#9B9999"
              postfixColor="#9B9999"
              customValue={props?.item?.price}
            />
          </Flex>
        </div>
        <div className="dailysale-item-divider" />
        <div className="flex items-center justify-between">
          <div>
            <span className="dailysale-time-label">
              {t('common:labels.start-time')}:
            </span>
            <span className="dailysale-time-text">
              {timeZeroFix(props.item.startTime.timeStr)}
            </span>
          </div>
          <div>
            <span className="dailysale-time-label">
              {t('common:labels.end-time')}:
            </span>
            <span className="dailysale-time-text">
              {timeZeroFix(props.item.finishTime.timeStr)}
            </span>
          </div>
        </div>
        <div className="dailysale-item-divider" />
        <div className="flex items-center justify-between">
          <Flex gap={4}>
            <span className="dailysale-time-label">{t('common:labels.remaining')}:</span>
            <Flex gap={4} className="dailysale-time-text">
              <span>{(props?.item?.count - props?.item?.soldCount) || 0}</span>
              <span>{t('common:labels.no-')}</span>
            </Flex>
          </Flex>
          {isActive && (
            <>
              <Button
                appVariant="submit"
                className="dailysale-forget-btn swiper-action-button hover:text-white"
                onClick={onForgetButtonClick}
              >
                <Flex justify="center" align="center">
                  <AiOutlineQrcode style={{ fontSize: '1.2rem' }} />
                  {t('common:labels.forget')}
                </Flex>
              </Button>
              <div className="dailysale-badge">
                <BsCheckLg />
              </div>
            </>
          )}
        </div>
      </Flex>
    </div>
  );
};

export default DailySellListItem;
